import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { useTRPC } from '@/shared/api';
import { PasswordField } from '@/shared/ui/password-field';

type Step = 'request' | 'sent' | 'reset' | 'done';

/**
 * 폼에서 문자열 값을 읽는다.
 *
 * `FormData.get` 은 `string | File` 을 준다 — 그대로 String() 에 넣으면 파일이 왔을 때
 * `[object File]` 이 비밀번호로 전송된다. 타입을 좁혀 그 경로를 없앤다.
 */
const readText = (form: FormData, name: string): string => {
  const value = form.get(name);
  return typeof value === 'string' ? value : '';
};

/**
 * 비밀번호 재설정 — 와이어프레임 P-02B (`/reset-password`).
 *
 * 와이어프레임이 단계를 나란히 그린 것은 설명용이고, 실제로는 순차로 나타난다.
 * 링크 만료·이미 사용된 링크 화면을 반드시 둔다 —
 * 이게 없으면 사용자는 막다른 길에서 이탈한다.
 *
 * **링크 수명을 화면이 알고 있지 않는다.** 만료 판정은 서버가 하고 화면은 그 문구를
 * 그대로 보여준다 — 양쪽에 수명을 적어 두면 한쪽만 고쳐져 어긋난다.
 */
export function ResetPasswordPage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [step, setStep] = useState<Step>(() => {
    return token ? 'reset' : 'request';
  });

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[26rem] flex-col gap-6">
        <h1 className="text-center text-[1.625rem] font-bold tracking-[-0.04em]">
          비밀번호 재설정
        </h1>

        {step === 'request' ? <RequestForm onSent={() => setStep('sent')} /> : null}
        {step === 'sent' ? <SentNotice onRetry={() => setStep('request')} /> : null}
        {step === 'reset' && token !== null ? (
          <ResetForm
            token={token}
            onDone={() => setStep('done')}
            onRestart={() => setStep('request')}
          />
        ) : null}
        {step === 'done' ? <DoneNotice /> : null}

        <p className="text-center text-[13px] text-muted-foreground">
          <Link to="/login" className="underline underline-offset-4">
            로그인으로 돌아가기
          </Link>
        </p>
      </div>
    </div>
  );
}

/**
 * 재설정 링크 요청.
 *
 * 결과를 구분해 보여주지 않는다 — 서버가 가입 여부와 무관하게 같은 응답을 준다
 * (계정 열거 방지). 여기서 "그런 계정 없음"을 표시하면 서버가 감춘 사실을 화면이 알려준다.
 */
function RequestForm({ onSent }: { onSent: () => void }): ReactElement {
  const trpc = useTRPC();
  const [email, setEmail] = useState('');
  const request = useMutation(
    trpc.auth.requestPasswordReset.mutationOptions({ onSuccess: onSent }),
  );

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        request.mutate({ email });
      }}
    >
      <p className="text-[14px] text-muted-foreground">가입한 이메일로 재설정 링크를 보냅니다.</p>
      <Field
        id="reset-email"
        type="email"
        label="이메일"
        placeholder="name@example.com"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Button type="submit" size="lg" className="w-full" disabled={request.isPending}>
        재설정 링크 받기
      </Button>
      {request.error === null ? null : (
        <p className="text-[12.5px] text-destructive">{request.error.message}</p>
      )}
    </form>
  );
}

function SentNotice({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border p-6 text-center">
      <p className="text-[15px] font-semibold">메일함을 확인해 주세요</p>
      <p className="text-[13px] text-muted-foreground">
        가입된 주소라면 재설정 링크를 보냈습니다.
        <br />
        메일이 오지 않았다면 스팸함을 확인해 주세요.
      </p>
      <Button variant="secondary" onClick={onRetry}>
        다시 보내기
      </Button>
    </div>
  );
}

/**
 * 새 비밀번호 설정.
 *
 * 두 값이 다르면 **서버를 부르지 않는다** — 오타는 왕복 없이 그 자리에서 알려주는 편이
 * 빠르고, 틀린 요청으로 시도 제한을 소모하지도 않는다.
 */
function ResetForm({
  token,
  onDone,
  onRestart,
}: {
  token: string;
  onDone: () => void;
  /** 만료·재사용은 서버만 안다 — 실패 문구 옆에서 새 링크 요청으로 되돌린다. */
  onRestart: () => void;
}): ReactElement {
  const trpc = useTRPC();
  const [mismatch, setMismatch] = useState(false);
  const reset = useMutation(trpc.auth.resetPassword.mutationOptions({ onSuccess: onDone }));

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const next = readText(form, 'next');
        const isMismatch = next !== readText(form, 'confirm');
        setMismatch(isMismatch);
        if (isMismatch) {
          return;
        }
        reset.mutate({ token, password: next });
      }}
    >
      <p className="text-[14px] text-muted-foreground">새 비밀번호를 입력해 주세요.</p>
      <PasswordField
        id="reset-new"
        name="next"
        label="새 비밀번호"
        placeholder="••••••••"
        required
      />
      <PasswordField
        id="reset-confirm"
        name="confirm"
        label="한 번 더 입력"
        placeholder="••••••••"
        required
        error={mismatch ? '두 비밀번호가 일치하지 않습니다.' : undefined}
      />
      <Button type="submit" size="lg" className="w-full" disabled={reset.isPending}>
        비밀번호 변경
      </Button>
      <p className="text-center text-[12.5px] text-muted-foreground">
        변경하면 모든 기기에서 로그아웃됩니다.
      </p>
      {reset.error === null ? null : (
        <div className="flex flex-col gap-3 rounded-lg border border-destructive border-l-[3px] p-4">
          <p className="text-[13px] text-destructive">{reset.error.message}</p>
          <Button type="button" variant="secondary" onClick={onRestart}>
            새 링크 받기
          </Button>
        </div>
      )}
    </form>
  );
}

/**
 * 재설정을 마치면 이메일 인증도 함께 끝난다 — 링크를 되돌려 보낸 것이 곧 메일함
 * 통제의 증명이라, 서버가 같은 UPDATE 로 처리한다. 그래서 바로 로그인으로 보낸다.
 */
function DoneNotice(): ReactElement {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border p-6 text-center">
      <p className="text-[15px] font-semibold">비밀번호를 바꿨습니다</p>
      <p className="text-[13px] text-muted-foreground">
        새 비밀번호로 로그인해 주세요. 다른 기기의 로그인은 모두 끊었습니다.
      </p>
      <Link
        to="/login"
        className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/85"
      >
        로그인하러 가기
      </Link>
    </div>
  );
}
